import React, { useMemo } from 'react';
import { Category, EventScheduleFragment } from '@graphql/generated';
import type { ScheduleListTranslations } from '../ScheduleList.i18n';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useHotelRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { useTestIds } from '../ScheduleList.utils';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { TextV2 } from '@withjoy/joykit';
import { hed3ToSubHeadingOverride, hed4ToSubHeadingOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { pxToRem } from '@withjoy/joykit/theme';
import { StyledVirtualEventContainer, styles } from '../ScheduleList.styles';
import { VirtualEventBlock } from '../../../components/VirtualEventBlock';
import { ContentColumns } from '../../../primitives';
import { SecondaryBlock } from './SecondaryBlock';
import { PrimaryBlock } from './PrimaryBlock';
import { config } from '@static/js/env.config';
import type { Variant, VariantStyles } from '../ScheduleList.types';

type ScheduleListItemProps = Readonly<{
  eventId: string;
  eventHandle: string;
  item: EventScheduleFragment;
  variantStyles: Readonly<Record<Variant, VariantStyles>>;
  variant: Variant;
  isHeaderInSecondaryBlock?: boolean;
  translations: ScheduleListTranslations;
  showTimeZone: boolean;
  isToBeAnnounced: boolean;
  onAddressClicked?: (title: string) => void;
  onCalendarClicked?: (title: string, type: string) => void;
  onVirtualEventBlockClicked?: () => void;
  scheduleListVersion: 'v1' | 'v2';
  header?: string;
  groupKey: string | number;
}>;

export const ScheduleListItem: React.FC<ScheduleListItemProps> = React.memo(
  ({
    eventId,
    eventHandle,
    item,
    isHeaderInSecondaryBlock,
    isToBeAnnounced = false,
    translations,
    showTimeZone,
    onAddressClicked,
    onCalendarClicked,
    onVirtualEventBlockClicked,
    scheduleListVersion,
    variantStyles,
    variant,
    header,
    groupKey,
  }) => {
    const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } }, false);
    const { bookYourStay } = useHotelRoutePaths();
    const { id, startTime, endTime, name, virtualEventLink, description, dressCode, location, placeId, showMap } = item;
    const startTimeMs = startTime?.milliseconds;
    const endTimeMs = endTime?.milliseconds;
    const timezoneIANA = startTime?.timezone;
    const timeframe = translations.timeframeTz(showTimeZone, startTimeMs, endTimeMs, timezoneIANA);
    const { listItemTestIds: testIds } = useTestIds(id);

    const styleProps = useMemo(() => variantStyles[variant], [variantStyles, variant]);
    const isScheduleListV1 = scheduleListVersion === 'v1';
    const isScheduleListV2 = scheduleListVersion === 'v2';

    const [hideWhereToStayLink, loadingWhereToStayLinkUserConsentKey] = useIsFeatureEnabled({
      consentKey: 'scheduleOptOutWhereToStayLink',
    });
    const [hideAddToCalendar, loadingAddToCalendarUserConsentKey] = useIsFeatureEnabled({
      consentKey: 'scheduleOptOutAddToCalendar',
    });

    const shouldShowWhereToStayButton = !hideWhereToStayLink && !loadingWhereToStayLinkUserConsentKey;
    const shouldShowAddToCalendarButton = !hideAddToCalendar && !loadingAddToCalendarUserConsentKey;

    const calendarDescription = useMemo(
      () => translations.description(bookYourStay, item.description, item.dressCode, eventHandle, config.clientUri, shouldShowWhereToStayButton),
      [eventHandle, translations, bookYourStay, item.dressCode, item.description, shouldShowWhereToStayButton],
    );
    const { scaleFontSize } = useScaleGuestSiteFontSize();

    const eventNameMarkup = (
      <GuestSiteTypographyOverride override={hed3ToSubHeadingOverride}>
        <TextV2
          {...testIds.eventName.prop}
          marginTop={{ sm4: '-1.5px' }}
          typographyVariant="hed3"
          lineHeight={1.35}
          fontWeight={500}
          fontSize={scaleFontSize(pxToRem(20), Category.SUB_HEADING)}
          // minHeight is used to ensure the placement of content below it will remain in the same position even if name is empty
          minHeight={`${Number.parseFloat(scaleFontSize(pxToRem(20), Category.SUB_HEADING)) * 1.35}rem`}
          wordBreak="break-word"
          {...(isScheduleListV1 ? { textAlign: ['center', 'center', 'inherit'], marginBottom: '0.875rem' } : { textAlign: 'left' })}
        >
          {name}
        </TextV2>
      </GuestSiteTypographyOverride>
    );
    const virtualEventMarkup = virtualEventLink ? (
      <StyledVirtualEventContainer
        __css={styles.virtualEventContainer}
        {...testIds.livestream.prop}
      >
        <VirtualEventBlock
          onVirtualEventBlockClicked={onVirtualEventBlockClicked}
          startTime={startTime}
          endTime={endTime}
          virtualEvent={virtualEventLink}
        />
      </StyledVirtualEventContainer>
    ) : undefined;

    const isSecondaryBlockHasContent = !!(virtualEventLink || location || dressCode || description || timeframe);

    return (
      <ContentColumns
        centered={true}
        role="listitem"
        key={id}
        {...{ ...styleProps.scheduleDayItemContainer, ...(!isSecondaryBlockHasContent ? styles.columnReverseContainer : {}) }}
      >
        {isScheduleListV2 && (
          <ContentColumns.Column {...styleProps.scheduleDateColumn(isToBeAnnounced)}>
            <GuestSiteTypographyOverride override={hed4ToSubHeadingOverride}>
              <TextV2
                data-testid={`groupheader-${groupKey}`}
                typographyVariant="hed4"
                textTransform="unset"
                letterSpacing="-0.32px"
                fontWeight={600}
                marginBottom={header ? 9 : 0}
                {...styleProps.headerText}
              >
                {header}
              </TextV2>
            </GuestSiteTypographyOverride>
          </ContentColumns.Column>
        )}
        {!isToBeAnnounced && (
          <ContentColumns.Column {...styleProps.secondaryBlockColumns}>
            <SecondaryBlock
              scheduleListVersion={scheduleListVersion}
              styleProps={isSecondaryBlockHasContent ? styleProps.secondaryBlock : variantStyles['stack'].secondaryBlock}
              testIds={testIds}
              item={item}
              timeframe={isToBeAnnounced ? undefined : timeframe}
              {...(isScheduleListV1 && {
                eventNameMarkup: isHeaderInSecondaryBlock ? eventNameMarkup : undefined,
                translations: translations,
                virtualEventMarkup: isHeaderInSecondaryBlock ? virtualEventMarkup : undefined,
                isMobile: isMobile,
                description: calendarDescription,
                onCalendarClicked: onCalendarClicked,
                shouldShowAddToCalendarButton: shouldShowAddToCalendarButton,
              })}
            />
          </ContentColumns.Column>
        )}

        <ContentColumns.Column {...styleProps.primaryBlockColumns}>
          <PrimaryBlock
            scheduleListVersion={scheduleListVersion}
            title={name}
            description={description}
            dressCode={dressCode}
            eventNameMarkup={isScheduleListV1 && isHeaderInSecondaryBlock && !isToBeAnnounced ? undefined : eventNameMarkup}
            isToBeAnnounced={isToBeAnnounced}
            translations={translations}
            location={location}
            placeId={placeId}
            showMap={showMap}
            styleProps={styleProps.primaryBlock}
            testIds={testIds}
            virtualEventMarkup={isScheduleListV1 && isHeaderInSecondaryBlock ? undefined : virtualEventMarkup}
            onAddressClicked={onAddressClicked}
            calendarDescription={calendarDescription}
            onCalendarClicked={onCalendarClicked}
            isMobile={isMobile}
            timeframe={isToBeAnnounced ? undefined : timeframe}
            item={item}
            eventHandle={eventHandle}
            eventId={eventId}
            shouldShowWhereToStayButton={shouldShowWhereToStayButton}
            shouldShowAddToCalendarButton={shouldShowAddToCalendarButton}
          />
        </ContentColumns.Column>
      </ContentColumns>
    );
  },
);
